import React from "react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Info, X, Filter } from "lucide-react";

const SKEL_WIDTHS = [
  ["w-14","w-44","w-28"], ["w-10","w-36","w-24"], ["w-16","w-52","w-32"],
  ["w-12","w-40","w-20"], ["w-14","w-48","w-28"], ["w-10","w-36","w-24"],
  ["w-16","w-44","w-32"], ["w-12","w-52","w-20"], ["w-14","w-40","w-28"], ["w-10","w-44","w-24"],
];

export function StageVelocityTile({ velocity, title, subtitle, tooltip }) {
  return (
    <TooltipProvider delayDuration={200}>
      <div
        className="shrink-0 hidden md:block rounded-lg rl-glass-edge bg-[hsl(var(--card))] px-5 py-3"
        data-testid="stage-velocity-tile"
      >
        {/* Header row: title + info icon */}
        <div className="flex items-center gap-1.5 mb-3">
          <span className="text-[10px] uppercase font-bold tracking-widest text-[hsl(var(--muted-foreground))]">
            {title}
          </span>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="inline-flex items-center opacity-50 hover:opacity-100 transition-opacity"
                aria-label="How this is calculated"
              >
                <Info className="w-3 h-3 text-[hsl(var(--muted-foreground))]" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" sideOffset={6} className="max-w-[240px] text-[11px] leading-relaxed">
              {tooltip}
            </TooltipContent>
          </Tooltip>
        </div>

        {/* 3-col stat grid */}
        <div className="grid grid-cols-3 gap-3">
          {[
            { n: velocity.today, label: "Today" },
            { n: velocity.week,  label: "This week" },
            { n: velocity.month, label: "This month" },
          ].map(({ n, label }) => (
            <div key={label}>
              <div className="font-mono-data text-lg font-semibold text-[hsl(var(--foreground))] leading-tight tabular-nums">
                {n}
              </div>
              <div className="text-[10px] text-[hsl(var(--muted-foreground))] mt-0.5">
                {label}
              </div>
            </div>
          ))}
        </div>

        {/* Subtitle */}
        <div className="text-[10px] text-[hsl(var(--muted-foreground))] mt-2.5">
          {subtitle}
        </div>
      </div>
    </TooltipProvider>
  );
}

export function DensityHintBubble({ kind, onDismiss }) {
  // "try-compact"  → nudge toward the Compact button (left button)
  // "try-standard" → nudge toward the Standard button (right button)
  const isCompact = kind === "try-compact";
  return (
    <div className="absolute top-[120%] -mt-1 right-0 w-[180px] bg-[hsl(var(--foreground))] text-[hsl(var(--background))] text-xs rounded-lg shadow-xl animate-in fade-in zoom-in-95 slide-in-from-top-2 duration-300 z-50">
      {/* Caret pointing up (shifts left/right based on target) */}
      <div 
        className="absolute -top-1.5 w-3 h-3 bg-[hsl(var(--foreground))] rotate-45"
        style={{ right: isCompact ? '48px' : '16px' }}
      />
      <div className="relative p-3">
        <button
          onClick={onDismiss}
          className="absolute top-1.5 right-1.5 p-1 text-[hsl(var(--background))]/60 hover:text-[hsl(var(--background))] hover:bg-[hsl(var(--background))]/10 rounded-md transition-colors"
          aria-label="Dismiss hint"
        >
          <X className="w-3 h-3" />
        </button>
        <p className="font-medium mb-1">
          {isCompact ? "Try Compact Mode" : "Try Standard View"}
        </p>
        <p className="text-[10px] opacity-80 leading-snug pr-4">
          {isCompact 
            ? "We noticed you have many items. This view fits more on screen." 
            : "Room to breathe. Good for smaller queues."}
        </p>
        <div className="mt-2.5 flex items-center justify-between">
          <span className="text-[9px] uppercase font-bold tracking-widest opacity-50">
            Keyboard
          </span>
          <div className="flex gap-1">
            <span className="px-1 py-0.5 rounded bg-[hsl(var(--background))]/10 font-mono-data text-[9px]">Z</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export function TableSkeleton({ stage, showValue, rows = 10, stageColor }) {
  return (
    <div className="relative w-full overflow-hidden" data-testid="stage-table-skeleton">
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse whitespace-nowrap min-w-[900px]">
          <thead>
            <tr>
              <th className="font-heading text-[10px] uppercase tracking-widest text-[hsl(var(--muted-foreground))] px-4 py-3 border-b border-[hsl(var(--border))] font-bold w-[120px]">PO No.</th>
              <th className="font-heading text-[10px] uppercase tracking-widest text-[hsl(var(--muted-foreground))] px-4 py-3 border-b border-[hsl(var(--border))] font-bold w-[180px]">Client</th>
              <th className="font-heading text-[10px] uppercase tracking-widest text-[hsl(var(--muted-foreground))] px-4 py-3 border-b border-[hsl(var(--border))] font-bold min-w-[200px]">Part Description</th>
              <th className="font-heading text-[10px] uppercase tracking-widest text-[hsl(var(--muted-foreground))] px-4 py-3 border-b border-[hsl(var(--border))] font-bold w-[100px]">Quantity</th>
              <th className="font-heading text-[10px] uppercase tracking-widest text-[hsl(var(--muted-foreground))] px-4 py-3 border-b border-[hsl(var(--border))] font-bold w-[130px]">Target Date</th>
              <th className="font-heading text-[10px] uppercase tracking-widest text-[hsl(var(--muted-foreground))] px-4 py-3 border-b border-[hsl(var(--border))] font-bold w-[130px]">Status</th>
              {showValue && (
                <th className="font-heading text-[10px] uppercase tracking-widest text-[hsl(var(--muted-foreground))] px-4 py-3 border-b border-[hsl(var(--border))] font-bold w-[100px]">Est. Value</th>
              )}
              <th className="font-heading text-[10px] uppercase tracking-widest text-[hsl(var(--muted-foreground))] px-4 py-3 border-b border-[hsl(var(--border))] font-bold w-[120px] text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: rows }).map((_, i) => {
              const widths = SKEL_WIDTHS[i % SKEL_WIDTHS.length];
              const delay = `${i * 40}ms`;
              return (
                <tr key={i} className="border-b border-[hsl(var(--border))]/50">
                  <td className="px-4 py-3">
                    <div className={`h-4 ${widths[0]} rounded animate-pulse bg-[hsl(var(--muted))]`} style={{ animationDelay: delay }} />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-full animate-pulse bg-[hsl(var(--muted))]" style={{ animationDelay: delay }} />
                      <div className={`h-4 ${widths[1]} rounded animate-pulse bg-[hsl(var(--muted))]`} style={{ animationDelay: delay }} />
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className={`h-4 ${widths[2]} rounded animate-pulse bg-[hsl(var(--muted))]`} style={{ animationDelay: delay }} />
                    <div className={`h-3 w-16 mt-1.5 rounded animate-pulse bg-[hsl(var(--muted))]/50`} style={{ animationDelay: delay }} />
                  </td>
                  <td className="px-4 py-3">
                    <div className="h-4 w-8 rounded animate-pulse bg-[hsl(var(--muted))] mb-1" style={{ animationDelay: delay }} />
                    <div className="h-3 w-12 rounded animate-pulse bg-[hsl(var(--muted))]/50" style={{ animationDelay: delay }} />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <div className="w-3.5 h-3.5 rounded-full animate-pulse bg-[hsl(var(--muted))]" style={{ animationDelay: delay }} />
                      <div className="h-4 w-16 rounded animate-pulse bg-[hsl(var(--muted))] mb-0.5" style={{ animationDelay: delay }} />
                    </div>
                    <div className="h-2.5 w-12 rounded animate-pulse bg-[hsl(var(--muted))]/50 ml-5" style={{ animationDelay: delay }} />
                  </td>
                  <td className="px-4 py-3">
                    <div className="h-6 w-24 rounded-full animate-pulse bg-[hsl(var(--muted))] opacity-70" style={{ animationDelay: delay }} />
                  </td>
                  {showValue && (
                    <td className="px-4 py-3">
                      <div className="h-4 w-14 rounded animate-pulse bg-[hsl(var(--muted))] mb-1" style={{ animationDelay: delay }} />
                      <div className="h-3 w-10 rounded animate-pulse bg-[hsl(var(--muted))]/50" style={{ animationDelay: delay }} />
                    </td>
                  )}
                  <td className="px-4 py-3">
                    <div className="flex gap-1.5 justify-end">
                      <div className="h-7 w-7 rounded animate-pulse bg-[hsl(var(--muted))]" style={{ animationDelay: delay }} />
                      <div className="h-7 w-7 rounded animate-pulse bg-[hsl(var(--muted))]" style={{ animationDelay: `${i * 40 + 40}ms` }} />
                      <div className="h-7 w-20 rounded animate-pulse" style={{ backgroundColor: stageColor + "30", animationDelay: `${i * 40 + 80}ms` }} />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function StatChip({ label, count, color, active, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group/sc inline-flex items-center gap-1 px-2 py-1 rounded-md transition-all duration-150 cursor-pointer select-none focus:outline-none focus-visible:ring-2"
      style={{
        background: active ? `${color}18` : "transparent",
        color:      active ? color        : "hsl(var(--muted-foreground))",
      }}
      onMouseEnter={(e) => { if (!active) { e.currentTarget.style.background = `${color}0d`; e.currentTarget.style.color = color; } }}
      onMouseLeave={(e) => { if (!active) { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "hsl(var(--muted-foreground))"; } }}
      data-testid={testId}
    >
      <span className="font-bold tabular-nums">{count}</span>
      <span className="opacity-80 group-hover/sc:opacity-100 transition-opacity">{label}</span>
      <Filter
        className="w-2.5 h-2.5 transition-all duration-150"
        style={{
          opacity: active ? 1 : 0,
          transform: active ? "translateX(0) scale(1)" : "translateX(-4px) scale(0.7)",
          marginLeft: active ? "2px" : "0",
        }}
      />
    </button>
  );
}
